import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { User2, Mail, Coins, ShieldCheck, Trash2, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  if (!user) return null;

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().split("T")[0];
      a.href = url;
      a.download = `hairmaxxingpro-my-data-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded.");
    } catch (err) {
      toast.error("Couldn't export your data. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== "DELETE") return;
    setBusy(true);
    setError("");
    try {
      await api.delete("/auth/account");
      // Clear local state and bounce to landing
      await logout();
      navigate("/", { replace: true });
    } catch (err) {
      console.error("Delete account failed:", err);
      setError("Couldn't delete your account. Please try again or contact Support@hairmaxxingpro.com.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16 animate-fade-in">
      <p className="cm-label">ACCOUNT</p>
      <h1 className="cm-heading text-4xl sm:text-5xl mt-3">Your <span className="text-copper">profile</span>.</h1>

      <div className="mt-10 cm-card p-8 space-y-6">
        <div className="flex items-center gap-3">
          <User2 className="w-5 h-5 text-copper" />
          <div>
            <p className="cm-label">Name</p>
            <p className="text-lg">{user.name || "—"}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Mail className="w-5 h-5 text-copper" />
          <div>
            <p className="cm-label">Email</p>
            <p className="text-lg">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Coins className="w-5 h-5 text-copper" />
          <div>
            <p className="cm-label">Scans today</p>
            <p className="text-lg">
              {(user.scans_used_today ?? 0)} / {user.daily_limit ?? 5}
            </p>
          </div>
        </div>
        {user.role === "admin" && (
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-copper" />
            <div>
              <p className="cm-label">Role</p>
              <p className="text-lg uppercase">{user.role}</p>
            </div>
          </div>
        )}
      </div>

      {/* Data portability — GDPR / CCPA right to know */}
      <div className="mt-10 cm-card p-8">
        <div className="flex items-center gap-2 mb-2">
          <Download className="w-5 h-5 text-copper" aria-hidden="true" />
          <p className="cm-label">YOUR DATA</p>
        </div>
        <h2 className="cm-heading text-2xl mt-3">Export a copy of your data</h2>
        <p className="text-sm text-gray-400 mt-3 leading-relaxed max-w-lg">
          Download everything HairMaxxing Pro holds on your account as a
          single JSON file — your profile, scan results, consent records,
          and payment history. Compliant with CCPA §1798.100 and
          GDPR Article 20.
        </p>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="cm-btn-secondary mt-6 inline-flex items-center gap-2"
          data-testid="account-export-button"
          aria-label="Export my data as JSON"
        >
          <Download className="w-4 h-4" aria-hidden="true" />
          {exporting ? "Preparing…" : "Export my data (JSON)"}
        </button>
      </div>

      {/* Danger zone */}
      <div className="mt-12 cm-card p-8 border-red-900/50">
        <div className="flex items-center gap-2 mb-2">
          <Trash2 className="w-5 h-5 text-red-400" />
          <p className="cm-label text-red-400">DANGER ZONE</p>
        </div>
        <h2 className="cm-heading text-2xl mt-3">Delete your account</h2>
        <p className="text-sm text-gray-400 mt-3 leading-relaxed max-w-lg">
          This permanently erases your account, your scan history, and every saved result.
          This cannot be undone.
        </p>

        {!confirming ? (
          <button
            onClick={() => setConfirming(true)}
            className="cm-btn-secondary mt-6 inline-flex items-center gap-2 border-red-900/60 hover:border-red-500 hover:text-red-400"
            data-testid="account-delete-button"
          >
            <Trash2 className="w-4 h-4" />
            Delete my account
          </button>
        ) : (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-gray-300">
              Type <span className="text-red-400 font-mono font-bold">DELETE</span> to confirm.
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="cm-input max-w-xs"
              placeholder="DELETE"
              data-testid="account-delete-confirm-input"
            />
            {error && (
              <div className="flex items-start gap-2 text-sm text-red-400" data-testid="account-delete-error">
                <AlertTriangle className="w-4 h-4 mt-0.5" /> {error}
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                disabled={confirmText !== "DELETE" || busy}
                className="cm-btn-primary inline-flex items-center gap-2"
                style={{ background: "linear-gradient(135deg,#f87171,#dc2626)", color: "#fff", border: "1px solid #fca5a5" }}
                data-testid="account-delete-confirm-button"
              >
                <Trash2 className="w-4 h-4" />
                {busy ? "Deleting…" : "Permanently delete"}
              </button>
              <button
                onClick={() => {
                  setConfirming(false);
                  setConfirmText("");
                  setError("");
                }}
                className="cm-btn-secondary"
                data-testid="account-delete-cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
